import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { App } from './App'
import { session } from './lib/api'
import { canonicalAddress, isOtherOrgSignIn, orgSegment } from './lib/orgPath'
import './styles.css'

const container = document.getElementById('root')
if (!container) throw new Error('The root element is missing from index.html.')

// A signed-in address that does not carry the right organization is corrected before anything
// renders, in place. Replacing the history entry rather than loading the canonical address means
// the page opened once: an unprefixed bookmark costs nothing, and a script that opened it and went on
// to use the page - the end-to-end suite does this throughout - is not racing a second load it did
// not ask for. `App` still carries its own guard for an address that goes wrong after this point.
const identity = session.identity()
if (identity && !isOtherOrgSignIn(identity, window.location.pathname)) {
  const { pathname, search, hash } = window.location
  const canonical = canonicalAddress(identity, pathname, search, hash)
  if (canonical) window.history.replaceState(window.history.state, '', canonical)
}

// The organization in the address, read once, because a router cannot change its basename in place.
// Taken as it appears rather than lower-cased: react-router compares a basename case-insensitively,
// and `App` rewrites a segment in the wrong case to the canonical one (lib/orgPath.ts).
const segment = orgSegment(window.location.pathname)
const basename = segment ? `/${segment}` : ''

createRoot(container).render(
  <StrictMode>
    <BrowserRouter basename={basename || undefined}>
      <App basename={basename} />
    </BrowserRouter>
  </StrictMode>
)
